import { addCallback } from '../lib/teact/teactn';
import { addActionHandler, getActions } from '../global';

import type { ApiSessionData, ApiUpdateAuthorizationStateType, ApiUpdateConnectionStateType } from '../api/types';
import type { ActionReturnType, GlobalState } from '../global/types';
import type { SeatCheckResult } from './seatStatus';
import { ApiMediaFormat } from '../api/types';

import { getChatAvatarHash } from '../global/helpers/chats';
import { getMainUsername } from '../global/helpers/users';
import { selectUser } from '../global/selectors';
import { addUnreadCountersCallback, getAllNotificationsCount } from '../util/folderManager';
import * as mediaLoader from '../util/mediaLoader';
import { hasStoredSession, loadStoredSession, storeSession } from '../util/sessions';
import { callApi } from '../api/gramjs';
import { runSeatAutoLogin } from './seatAutoLogin';
import { clearSeatDeviceContext, type SeatDeviceContext, storeSeatDeviceContext } from './seatDevice';
import { isSessionRevokedError } from './seatForward';
import { runSeatSelfCheck } from './seatStatus';

type OutboundType = 'ready'
  | 'syncState'
  | 'updateTgAccountInfo'
  | 'onTgConnectionBroken'
  | 'showMessage'
  | 'seatChecked'
  | 'seatCooldown'
  | 'seatForwardResult'
  | 'requestForwardText'
  | 'checkSeatPhone'
  | 'seatUnread'
  | 'seatAvatar'
  | 'seatAutoReply'
  | 'requestAutoCode'
  | 'autoLoginResult'
  | 'seatLoginExit'
  | 'runnerSessionMinted'
  | 'claimAutoReply'
  | 'seatBoot'
  | 'seatSessionInvalid';

type RelayConfig = {
  relayBotId?: string;
  relayBotUsername?: string;
  maxRelayAge?: number;
};

// 自动回复配置由父页下发（一个用户一套），席位客户端只在浏览器里执行
export type AutoReplyRule = {
  keywords: string[];
  script: string;
  // 命中后按顺序分开发的多条消息；老配置没有，只用 script
  scripts?: string[];
};

export type AutoReplyPart = {
  type: 'text' | 'card' | 'todo';
  text?: string;
  phone?: string;
  name?: string;
  title?: string;
  items?: Array<{ id?: number; title: string }>;
  others_can_complete?: boolean;
};

export type AutoReplyConfig = {
  // 该席位号自己的开关：关了这个号收到私聊也不回
  enabled: boolean;
  contacts: boolean;
  strangers: boolean;
  delay: number;
  obfuscation: number;
  text: string;
  // 默认话术的多条消息，按顺序分开发；老配置没有，只用 text
  texts?: string[];
  // 默认话术带类型的完整列表（文字 / 名片 / 核对清单），有它就以它为准；texts 只含其中的文字
  parts?: AutoReplyPart[];
  // 每人多长时间内只回一次（分钟），0=不限制；默认话术、每条关键词话术各算各的，回复时间由服务端记
  onceMinutes?: number;
  rules: AutoReplyRule[];
};

// 私聊任务的内容：发文案、核对清单，或转发公开频道里的某条消息；
// 文案或核对清单配了推送名片时，内容成功后再发一条联系人名片（转发不发名片），
// 只配名片没配文案时是 card 任务，只发名片这一条
export type ForwardTask = {
  mode: 'text' | 'forward' | 'card' | 'todo';
  text?: string;
  sourceUsername?: string;
  sourceMessageId?: number;
  card?: ForwardCard;
  todo?: ForwardTodo;
};

export type ForwardCard = {
  phone: string;
  firstName: string;
};

export type ForwardTodo = {
  title: string;
  items: Array<{
    id: number;
    title: string;
  }>;
  othersCanComplete?: boolean;
};

type InboundMessage = RelayConfig & {
  type?: string;
  autoReply?: AutoReplyConfig;
  session?: string;
  taskId?: string;
  mode?: 'text' | 'forward' | 'card' | 'todo';
  sourceUsername?: string;
  sourceMessageId?: number;
  text?: string;
  cardPhone?: string;
  cardName?: string;
  todo?: ForwardTodo;
  isVip?: boolean;
  error?: string;
  phone?: string;
  isAllowed?: number;
  message?: string;
  skipLoginChecks?: boolean;
  enabled?: boolean;
  forwardGate?: boolean;
  apiUrl?: string;
  requestId?: string;
  code?: string;
  password?: string;
  retryAfter?: number;
  claimed?: number;
  until?: number;
};

type ForwardTextRequest = {
  resolve: (task: ForwardTask) => void;
  reject: (err: Error) => void;
};

type AutoCodeRequest = {
  resolve: (result: { code: string; password?: string }) => void;
  reject: (err: AutoCodeError) => void;
};

// 取码页面会限制刷新频率，它说的等待秒数要带到重试逻辑里，否则只会一直撞在限流上
export class AutoCodeError extends Error {
  retryAfter: number;

  constructor(message: string, retryAfter = 0) {
    super(message);
    this.retryAfter = retryAfter;
  }
}

type AccountInfo = {
  tgUserId: string;
  phone: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  session?: string;
  twoStepPassword?: string;
};

const PARENT_ORIGIN = import.meta.env.TG_SEAT_PARENT_ORIGIN;
const MESSAGE_SOURCE = 'tg-seat';
// The phone check must never brick the login form, so an unanswered check counts as allowed
const PHONE_CHECK_TIMEOUT = 8000;
// 占位查不到结果就当没占到：宁可漏回，不重复回
const AUTO_REPLY_CLAIM_TIMEOUT = 15000;

let lastAuthState: ApiUpdateAuthorizationStateType | undefined;
let lastConnectionState: ApiUpdateConnectionStateType | undefined;
let lastAccountKey: string | undefined;
let isOnlineReported = false;
let relayConfig: RelayConfig = {};
let autoReplyConfig: AutoReplyConfig | undefined;
let twoStepPassword: string | undefined;
let isSessionRestoreAttempted = false;
let isSelfCheckDone = false;
// 铸第二份授权要开一个额外连接，同一时间只允许一个在跑，避免父页重复要求时铸出多份
let isRunnerSessionMinting = false;
let isVipAccount = false;
// 开了服务器托管的号在网页失联时由服务端常驻进程接管代发，此时本机必须停手：
// 同一个号两端同时发会重复私聊目标，判定只由服务端做，这里只执行父页转达的结论
let isForwardAllowed = true;
let lastUnreadCount: number | undefined;
let lastAvatarHash: string | undefined;
const forwardTextRequests = new Map<string, ForwardTextRequest>();
const phoneChecks = new Map<string, (errorMessage: string | undefined) => void>();
const autoCodeRequests = new Map<string, AutoCodeRequest>();
let autoCodeRequestCount = 0;
export type AutoReplyClaim = {
  claimed: boolean;
  // 占不到时，服务端给的下次能回的时间（秒级时间戳），0=不知道
  until: number;
};

const autoReplyClaims = new Map<string, (claim: AutoReplyClaim) => void>();
let autoReplyClaimCount = 0;

// 两步验证密码只在登录时经过浏览器，这里留一份交给父页落库，供换设备时免验证码恢复席位
addActionHandler('setAuthPassword', (global, actions, payload): ActionReturnType => {
  twoStepPassword = payload.password;
});

// 席位客户端是嵌在父页里的，只有这种情况下登录页才给“返回选登录方式”入口
export const IS_SEAT_EMBEDDED = Boolean(PARENT_ORIGIN) && window.parent !== window;

// 席位子域名是按席位编号分的、所有用户共用，同一台电脑换个用户登控制台时，
// 本地可能存着上一个用户的授权与聊天缓存。启动前先问父页这个席位该是谁的
// session，比对不一致就清掉本地旧数据，绝不带错号上线。
// 只清这台浏览器的本地缓存，不退出 Telegram 账号：旧用户的 session 还在服务端，
// 他回来登控制台时会重新下发恢复
export function verifySeatSessionOwner(): Promise<void> {
  if (!IS_SEAT_EMBEDDED) return Promise.resolve();

  return new Promise((resolve) => {
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      window.clearInterval(timer);
    };
    function onMessage(event: MessageEvent) {
      if (event.origin !== PARENT_ORIGIN || event.source !== window.parent) return;
      const data = event.data as { type?: string; session?: string } | undefined;
      if (data?.type !== 'seatBootSession') return;
      cleanup();
      void applyBootSession(data.session || '').finally(resolve);
    }
    window.addEventListener('message', onMessage, false);
    postToParent('seatBoot', {});
    // 父页可能还没注册监听，隔半秒重发；旧版控制台不认这条消息，
    // 超时时不碰本地数据、按原样启动（行为退回修改前，不会更差）
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (attempts >= 30) {
        cleanup();
        resolve();
        return;
      }
      postToParent('seatBoot', {});
    }, 500);
  });
}

// 比对本地存的授权与父页下发的是不是同一份 authKey：不是就清本地、换成下发的那份；
// 父页明确回空串（这个席位没有账号）则清完回登录页
async function applyBootSession(sessionJson: string) {
  const stored = loadStoredSession();

  let pushed: (ApiSessionData & { context?: SeatDeviceContext }) | undefined;
  if (sessionJson) {
    try {
      pushed = JSON.parse(sessionJson) as ApiSessionData & { context?: SeatDeviceContext };
    } catch (err) {
      pushed = undefined;
    }
    if (!pushed?.mainDcId || !pushed.keys?.[pushed.mainDcId]) pushed = undefined;
  }

  if (!stored) {
    if (pushed) {
      if (pushed.context) storeSeatDeviceContext(pushed.context);
      storeSession(pushed);
    }
    return;
  }

  const storedKey = stored.mainDcId ? stored.keys?.[stored.mainDcId] : undefined;
  if (pushed && storedKey && storedKey === pushed.keys[pushed.mainDcId]) return;

  // 对不上（换了用户 / 席位没账号 / 下发数据损坏）一律清本地：宁可回登录页，
  // 也不能带着不属于这个席位的号上线
  await wipeLocalData();
  if (pushed) {
    if (pushed.context) storeSeatDeviceContext(pushed.context);
    storeSession(pushed);
  }
}

// 整站本地数据全清：除授权外，缓存的全局状态/聊天记录/头像媒体里都是上一个
// 用户的隐私，不能留给下一个用户；此时应用还没初始化，没有打开着的连接
async function wipeLocalData() {
  localStorage.clear();
  try {
    const dbs = await window.indexedDB.databases?.() || [];
    const names = dbs.map((db) => db.name).filter((name): name is string => Boolean(name));
    await Promise.all(names.map((name) => new Promise<void>((resolve) => {
      const req = window.indexedDB.deleteDatabase(name);
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
    })));
    if (window.caches) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((key) => window.caches.delete(key)));
    }
  } catch (err) {
    // 媒体缓存清不掉也不阻塞启动，授权已随 localStorage 清除
  }
}

// 席位域名被单独打开时不能启动：本地存着授权数据，直接打开会绕过坐席控制台的
// 心跳与租约，服务端会当作网页离场而放行 runner 接管，同一份 authKey 两端同连即作废。
// 本地开发（localhost）不拦，方便调试
export function guardSeatStandalone() {
  const isLocalDev = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (!PARENT_ORIGIN || isLocalDev || window.parent !== window) return false;

  const root = document.getElementById('root');
  if (root) {
    root.innerHTML = '';
    const notice = document.createElement('div');
    notice.style.cssText = 'display:flex;flex-direction:column;align-items:center;justify-content:center;'
      + 'height:100vh;gap:12px;font:16px/1.6 system-ui,sans-serif;color:#333;text-align:center;padding:0 24px;';
    const title = document.createElement('div');
    title.style.cssText = 'font-size:20px;font-weight:600;';
    title.textContent = '请从坐席控制台进入';
    const sub = document.createElement('div');
    sub.style.cssText = 'color:#888;';
    sub.textContent = '席位聊天窗口只能在坐席控制台里使用，单独打开不会连接账号。';
    notice.append(title, sub);
    root.append(notice);
  }
  return true;
}

// 登录页左上角返回：父页收起这个席位的登录页，回到「请选择登录方式」，不拆 iframe
export function exitSeatLogin() {
  postToParent('seatLoginExit', {});
}

export function initSeatBridge() {
  if (!PARENT_ORIGIN || window.parent === window) return;

  window.addEventListener('message', handleParentMessage, false);
  addCallback(handleGlobalChange);
  // The seat card shows the same badge count the app shows itself, so it drops as chats are read
  addUnreadCountersCallback(reportUnreadCount);
}

function handleParentMessage(event: MessageEvent) {
  if (event.origin !== PARENT_ORIGIN || event.source !== window.parent) return;

  const data: InboundMessage | undefined = event.data;
  if (!data?.type) return;

  switch (data.type) {
    case 'startSync':
      relayConfig = {
        relayBotId: data.relayBotId,
        relayBotUsername: data.relayBotUsername,
        maxRelayAge: data.maxRelayAge,
      };
      if (data.forwardGate !== undefined) isForwardAllowed = data.forwardGate;
      if (data.session) restoreSession(data.session);
      reportState(true);
      break;
    case 'seatForwardGate':
      isForwardAllowed = data.enabled !== false;
      break;
    case 'autoReplyConfig':
      autoReplyConfig = data.autoReply;
      break;
    case 'logoutTgAccount':
      // 下一个号可能是验证码上的，不能继续用上一个协议包的设备指纹
      clearSeatDeviceContext();
      getActions().signOut({ forceInitApi: true });
      break;
    case 'forwardText':
      resolveForwardText(data);
      break;
    case 'seatPhoneChecked':
      resolvePhoneCheck(data);
      break;
    case 'autoLogin':
      startAutoLogin(data);
      break;
    case 'autoReplyClaimed':
      resolveAutoReplyClaim(data);
      break;
    case 'autoCode':
      resolveAutoCode(data);
      break;
    case 'mintRunnerSession':
      void mintRunnerSessionForParent(data.password);
      break;
    case 'onlineAck':
      // The parent page answers every `ready`, which is only reported when the seat goes online, so
      // the bot greeting runs per online transition and the self check once per page
      void runLoginChecks(data.skipLoginChecks);
      break;
    default:
      break;
  }
}

// 父页发现这个号还没有离线私聊专用的第二份授权时会让这边铸一份：
// 铸出来的是同一账号下另一把 authKey，网页照旧用自己那份，两份互不作废。
// 铸失败只回状态码给父页记账，不影响网页自己的登录状态
async function mintRunnerSessionForParent(password?: string) {
  if (isRunnerSessionMinting) return;
  isRunnerSessionMinting = true;

  try {
    const result = await callApi('mintRunnerSession', { twoStepPassword: password });
    postToParent('runnerSessionMinted', {
      session: result?.session,
      error: result?.session ? undefined : (result?.error || 'unknown'),
      // 只用于排查的原因文本（已在铸造侧过滤，不带授权数据与密码）
      detail: result?.session ? undefined : result?.detail,
    });
  } catch (err) {
    postToParent('runnerSessionMinted', {
      error: 'unknown',
      detail: err instanceof Error ? `bridge:${err.message}`.slice(0, 120) : 'bridge_call_failed',
    });
  } finally {
    isRunnerSessionMinting = false;
  }
}

// 换设备时父页下发上次的授权数据，写进本地后刷新即以已登录状态启动，不需要再走验证码
function restoreSession(sessionJson: string) {
  if (isSessionRestoreAttempted || hasStoredSession()) return;
  isSessionRestoreAttempted = true;

  let sessionData: (ApiSessionData & { context?: SeatDeviceContext }) | undefined;
  try {
    sessionData = JSON.parse(sessionJson) as ApiSessionData & { context?: SeatDeviceContext };
  } catch (err) {
    reportSeatMessage('席位会话数据无法解析，请重新登录账号', 'error');
    return;
  }

  if (!sessionData?.mainDcId || !sessionData.keys?.[sessionData.mainDcId]) {
    reportSeatMessage('席位会话数据不完整，请重新登录账号', 'error');
    return;
  }

  // 协议包上号的账号自带 api 凭据与设备参数，要在刷新前存好，重载后的 initApi 才用得上
  if (sessionData.context) storeSeatDeviceContext(sessionData.context);

  storeSession(sessionData);
  window.location.reload();
}

function reportUnreadCount() {
  const unread = getAllNotificationsCount();
  if (unread === lastUnreadCount) return;

  lastUnreadCount = unread;
  postToParent('seatUnread', { unread });
}

function handleGlobalChange(global: GlobalState) {
  const { state: authState } = global.auth;
  const { connectionState } = global;

  if (authState !== lastAuthState || connectionState !== lastConnectionState) {
    if (connectionState === 'connectionStateBroken' && lastConnectionState !== 'connectionStateBroken') {
      void reportSessionIfRevoked();
    }
    lastAuthState = authState;
    lastConnectionState = connectionState;
    reportState(false);
  }

  reportAccount(global);
  void reportAvatar(global);
}

// 连接断开时问 API 层是不是授权被 Telegram 作废了（普通断网不算）：作废了就告诉父页，
// 这一页里也不再导入父页下发的那份旧授权，免得反复导入、作废、刷新
async function reportSessionIfRevoked() {
  const session = loadStoredSession();
  const reason = await callApi('consumeSeatSessionRevoked');
  if (!reason) return;

  isSessionRestoreAttempted = true;
  postToParent('seatSessionInvalid', { reason, session: session ? JSON.stringify(session) : '' });
}

// The parent page runs on another origin and cannot read this origin's blob URLs, so the avatar is
// handed over as a data URL; accounts without a photo keep the initials placeholder
async function reportAvatar(global: GlobalState) {
  const { currentUserId } = global;
  if (!currentUserId) return;

  const user = selectUser(global, currentUserId);
  if (!user) return;

  const hash = getChatAvatarHash(user);
  if (hash === lastAvatarHash) return;

  lastAvatarHash = hash;
  if (!hash) {
    postToParent('seatAvatar', { avatar: undefined });
    return;
  }

  try {
    const blobUrl = await mediaLoader.fetch(hash, ApiMediaFormat.BlobUrl);
    const blob = await (await window.fetch(blobUrl)).blob();
    const avatar = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
    postToParent('seatAvatar', { avatar });
  } catch (err) {
    // A failed download must not stick: the next global change retries it
    lastAvatarHash = undefined;
  }
}

function reportState(isForced: boolean) {
  const authState = lastAuthState;
  const connectionState = lastConnectionState;

  postToParent('syncState', { authState, connectionState });

  const isOnline = getIsOnline();
  if (isOnline && (!isOnlineReported || isForced)) {
    isOnlineReported = true;
    postToParent('ready', {});
  } else if (!isOnline && (isOnlineReported || isForced)) {
    isOnlineReported = false;
    postToParent('onTgConnectionBroken', { authState, connectionState });
  }
}

function reportAccount(global: GlobalState) {
  const info = buildAccountInfo(global);
  if (!info) return;

  const key = JSON.stringify(info);
  if (key === lastAccountKey) return;

  lastAccountKey = key;
  postToParent('updateTgAccountInfo', info);
}

function buildAccountInfo(global: GlobalState): AccountInfo | undefined {
  const { currentUserId } = global;
  if (!currentUserId) return undefined;

  const user = selectUser(global, currentUserId);
  if (!user?.phoneNumber) return undefined;

  const session = loadStoredSession();

  return {
    tgUserId: user.id,
    phone: user.phoneNumber,
    username: getMainUsername(user),
    firstName: user.firstName,
    lastName: user.lastName,
    session: session ? JSON.stringify(session) : undefined,
    twoStepPassword,
  };
}

function getIsOnline() {
  return lastAuthState === 'authorizationStateReady' && lastConnectionState === 'connectionStateReady';
}

export function getSeatRelayConfig() {
  return relayConfig;
}

// 自动回复不受此开关影响：服务器托管一期只接管代发，回复仍只在网页里跑
export function getIsSeatForwardAllowed() {
  return isForwardAllowed;
}

export function getIsSeatAccountVip() {
  return isVipAccount;
}

export function getSeatAutoReplyConfig() {
  return autoReplyConfig;
}

// 自动回复发出后回传父页写运行日志（与代发结果同一条通道）
/** 每人多长时间内只回一次：发之前到服务端占位，占到了（时间内没回过）才 claimed */
export function claimSeatAutoReply(peerId: string, replyKey: string) {
  if (!PARENT_ORIGIN || window.parent === window) return Promise.resolve<AutoReplyClaim>({ claimed: false, until: 0 });

  autoReplyClaimCount += 1;
  const requestId = `${Date.now()}-${autoReplyClaimCount}`;

  return new Promise<AutoReplyClaim>((resolve) => {
    autoReplyClaims.set(requestId, resolve);
    postToParent('claimAutoReply', { requestId, peerId, replyKey });

    setTimeout(() => {
      if (autoReplyClaims.delete(requestId)) resolve({ claimed: false, until: 0 });
    }, AUTO_REPLY_CLAIM_TIMEOUT);
  });
}

/** 占了位却没发出去，把位置还回去 */
export function releaseSeatAutoReply(peerId: string, replyKey: string) {
  postToParent('claimAutoReply', { peerId, replyKey, release: 1 });
}

function resolveAutoReplyClaim(data: InboundMessage) {
  const { requestId } = data;
  if (!requestId) return;

  const resolve = autoReplyClaims.get(requestId);
  if (!resolve) return;
  autoReplyClaims.delete(requestId);

  resolve({ claimed: Boolean(data.claimed), until: Number(data.until) || 0 });
}

export function reportSeatAutoReply(targetName: string, errorMessage?: string) {
  postToParent('seatAutoReply', { targetName, errorMessage });
}

// The forwarding cooldown lives in the same server table the backend forwarder uses,
// so the account is skipped by both paths until it expires
export function reportSeatCooldown(waitSeconds: number, reason: string) {
  postToParent('seatCooldown', { waitSeconds, reason });
}

// The private message is sent from the browser, so the result is reported back and the server edits
// the bot push card with the same wording the server-side forwarder uses
export function reportSeatForwardResult(
  taskId: string | undefined, isSuccess: boolean, errorType?: string, errorMessage?: string,
) {
  postToParent('seatForwardResult', {
    taskId, isSuccess, errorType, errorMessage,
  });
}

/**
 * The account may already belong to another user or to the backend account pool, and that is only
 * visible server-side, so the phone is checked before the login code is requested
 *
 * @return the error message to show, or `undefined` when the account can be used
 */
export function checkSeatPhone(phone: string) {
  if (!PARENT_ORIGIN || window.parent === window) return Promise.resolve(undefined);

  return new Promise<string | undefined>((resolve) => {
    phoneChecks.set(phone, resolve);
    postToParent('checkSeatPhone', { phone });

    setTimeout(() => {
      if (phoneChecks.delete(phone)) resolve(undefined);
    }, PHONE_CHECK_TIMEOUT);
  });
}

function resolvePhoneCheck(data: InboundMessage) {
  const { phone } = data;
  if (!phone) return;

  const resolve = phoneChecks.get(phone);
  if (!resolve) return;
  phoneChecks.delete(phone);

  resolve(data.isAllowed ? undefined : (data.message || '该账号已被添加，请先删除或联系管理员'));
}

// 自动接码登录：手机号与取码页面地址由父页下发，登录本身走客户端原有的登录动作
function startAutoLogin(data: InboundMessage) {
  const phone = data.phone;
  const apiUrl = data.apiUrl;
  if (!phone || !apiUrl) return;

  runSeatAutoLogin(phone, apiUrl).then(() => {
    postToParent('autoLoginResult', { phone, isSuccess: true });
  }, (err: unknown) => {
    postToParent('autoLoginResult', {
      phone,
      isSuccess: false,
      errorMessage: describeError(err),
    });
  });
}

// 接码平台的取码页面与席位不同源，浏览器直接抓不到，交给父页调服务端代取
export function requestSeatAutoCode(apiUrl: string) {
  return new Promise<{ code: string; password?: string }>((resolve, reject) => {
    autoCodeRequestCount += 1;
    const requestId = String(autoCodeRequestCount);
    autoCodeRequests.set(requestId, { resolve, reject });
    postToParent('requestAutoCode', { requestId, apiUrl });
  });
}

function resolveAutoCode(data: InboundMessage) {
  const { requestId } = data;
  if (!requestId) return;

  const request = autoCodeRequests.get(requestId);
  if (!request) return;
  autoCodeRequests.delete(requestId);

  if (data.code) {
    request.resolve({ code: data.code, password: data.password });
  } else {
    request.reject(new AutoCodeError(data.error || '未取到验证码', data.retryAfter || 0));
  }
}

// Every seat account of a user shares one forwarding text, and the server randomizes it per task,
// so it is requested from the parent page for each relay task instead of being cached here
export function requestForwardText(taskId: string) {
  return new Promise<ForwardTask>((resolve, reject) => {
    forwardTextRequests.set(taskId, { resolve, reject });
    postToParent('requestForwardText', { taskId });
  });
}

function resolveForwardText(data: InboundMessage) {
  const { taskId } = data;
  if (!taskId) return;

  const request = forwardTextRequests.get(taskId);
  if (!request) return;
  forwardTextRequests.delete(taskId);

  const card: ForwardCard | undefined = data.cardPhone
    ? { phone: data.cardPhone, firstName: data.cardName || '' }
    : undefined;

  if (data.mode === 'forward' && data.sourceUsername && data.sourceMessageId) {
    request.resolve({
      mode: 'forward',
      sourceUsername: data.sourceUsername,
      sourceMessageId: data.sourceMessageId,
    });
  } else if (data.mode === 'todo' && data.todo) {
    request.resolve({ mode: 'todo', todo: data.todo, card });
  } else if (data.text) {
    request.resolve({ mode: 'text', text: data.text, card });
  } else if (card) {
    request.resolve({ mode: 'card', card });
  } else {
    request.reject(new Error(data.error || '未获取到私聊文案'));
  }
}

// A seat that cannot receive relay tasks must not stay online, so a failed greeting is reported as a
// broken connection and the parent page stops the heartbeat
async function runLoginChecks(skipLoginChecks?: boolean) {
  // An account stored as frozen or appeal-required keeps that status until the appeal or an admin
  // check clears it and is never picked for forwarding, so it is left completely alone: neither the
  // bot greeting nor the SpamBot check is worth bothering the account with
  if (skipLoginChecks) return;

  if (!await startRelayBotConversation()) {
    isOnlineReported = false;
    postToParent('onTgConnectionBroken', { authState: lastAuthState, connectionState: lastConnectionState });
    return;
  }

  void runSelfCheckOnce();
}

// GramJS rejects with plain objects (RPCError-like: { errorMessage, code }) as often as with Error,
// and String(obj) turns those into a useless "[object Object]" in the seat log, hiding the real
// reason (AUTH_KEY_DUPLICATED, PEER_ID_INVALID...), so pull the message out field by field
function describeError(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;

  if (err && typeof err === 'object') {
    const fields = err as Record<string, unknown>;
    const text = [fields.errorMessage, fields.message]
      .find((value): value is string => typeof value === 'string' && value !== '');
    const code = typeof fields.code === 'number' ? ` (code ${fields.code})` : '';
    if (text) return `${text}${code}`;

    try {
      const json = JSON.stringify(err);
      if (json && json !== '{}') return json;
    } catch {
      // 循环引用等无法序列化时退回默认描述
    }

    if (typeof fields.className === 'string' && fields.className) return `${fields.className}${code}`;
  }

  return String(err);
}

// A bot cannot write first, so the account greets the bot on every fresh login: a user who deletes
// or blocks the bot in between is silently recovered the next time the seat comes online
async function startRelayBotConversation() {
  const { relayBotUsername } = relayConfig;
  if (!relayBotUsername) {
    reportSeatMessage('未配置平台机器人用户名，无法与机器人建立会话，请联系管理员', 'error');
    return true;
  }

  let isStarted: true | undefined;
  try {
    isStarted = await callApi('startSeatRelayBot', { username: relayBotUsername });
  } catch (err) {
    // 登录已废时只报这个原因：这种情况发 /start 也没用，提示反而把真正要做的事（重新上号）盖住了
    const reason = describeError(err);
    reportSeatMessage(
      isSessionRevokedError(reason.toUpperCase())
        ? reason
        : `与机器人建立会话失败，请在本账号里给 @${relayBotUsername} 发送 /start：${reason}`,
      'error',
    );
    return false;
  }

  if (!isStarted) {
    reportSeatMessage(`未找到机器人 @${relayBotUsername}，私聊任务无法送达`, 'error');
    return false;
  }

  return true;
}

// Status and Premium are checked once the account comes online and stored server-side,
// so forwarding reads them from the database instead of rechecking on every flood error
async function runSelfCheckOnce() {
  if (isSelfCheckDone) return;
  isSelfCheckDone = true;

  let result: SeatCheckResult;
  try {
    result = await runSeatSelfCheck();
  } catch (err) {
    reportSeatMessage(`账号自检失败：${describeError(err)}`, 'error');
    return;
  }

  isVipAccount = result.isVip;

  postToParent('seatChecked', {
    isVip: result.isVip,
    loginStatus: result.loginStatus,
    errorMessage: result.errorMessage,
  });
}

// topic 决定这条日志在运行日志里归哪类，不传就归「客户端」
export function reportSeatMessage(
  message: string,
  level: 'info' | 'error' = 'info',
  topic?: 'forward',
) {
  postToParent('showMessage', { message, level, topic });
}

function postToParent(type: OutboundType, payload: AnyLiteral) {
  if (!PARENT_ORIGIN) return;

  window.parent.postMessage({ source: MESSAGE_SOURCE, type, ...payload }, PARENT_ORIGIN);
}
